import MultiPaxSession, { MultiPaxSessionId } from "models/session/session";
import { StateCreator } from "zustand";

import ChecklistItem, {
  ChecklistItemId,
} from "../../models/checklist/checklist-item/checklist-item";
import Journey from "../../models/journey/journey";
import PaxSession from "../../models/session/pax-session";
import { SlicedStore } from "../store";
import { ScannerType } from "models/scanner-type";
import config from "config/config";

export interface SessionSlice {
  paxSessions: PaxSession[];

  sessionId: MultiPaxSessionId;
  journey: Journey | null;

  updateSession: (updatedMultiPaxSession: MultiPaxSession) => void;
  updatePaxSession: (updatedPaxSession: PaxSession) => void;
  updatePaxSessions: (updatedPaxSessions: PaxSession[]) => void;
  getChecklistItemById: (
    itemId: ChecklistItemId,
    paxSessionId: string
  ) => ChecklistItem | null;

  selectedScannerType: ScannerType;
  setSelectedScannerType: (scannerType: ScannerType) => void;

  /*
  demo scenario
  */
  demoScenario: string | undefined;
  setDemoScenario: (demoScenario: string) => void;

  /*
  address
  */
  addressLine1: string;
  setAddressLine1: (line1: string) => void;

  addressState: string;
  setAddressState: (state: string) => void;

  addressCity: string;
  setAddressCity: (city: string) => void;

  addressPostalCode: string;
  setAddressPostalCode: (postalCode: string) => void;
}

export const createSessionSlice: StateCreator<
  SlicedStore,
  [],
  [],
  SessionSlice
> = (set, get) => ({
  paxSessions: [],

  sessionId: "",
  journey: null,

  updateSession: (updatedMultiPaxSession: MultiPaxSession) => {
    set({
      paxSessions: updatedMultiPaxSession.paxSessions,
      journey: updatedMultiPaxSession.journey,
      sessionId: updatedMultiPaxSession.sessionId,
    });
  },

  updatePaxSession: (updatedPaxSession: PaxSession) => {
    set((prevState) => ({
      paxSessions: prevState.paxSessions.map((paxSession) => {
        if (paxSession.paxSessionId === updatedPaxSession.paxSessionId) {
          return updatedPaxSession;
        }
        return paxSession;
      }),
    }));
  },

  updatePaxSessions: (updatedPaxSessions: PaxSession[]) => {
    set((prevState) => ({
      paxSessions: prevState.paxSessions.map((paxSession) => {
        const foundUpdatedSession = updatedPaxSessions.find(
          (updatedPaxSession) =>
            paxSession.paxSessionId === updatedPaxSession.paxSessionId
        );
        return foundUpdatedSession ? foundUpdatedSession : paxSession;
      }),
    }));
  },

  getChecklistItemById: (
    itemId: ChecklistItemId,
    paxSessionId: string
  ): ChecklistItem | null => {
    const paxSession = get().paxSessions.find(
      (p) => p.paxSessionId === paxSessionId
    );
    return paxSession?.checklist.getItemById(itemId) || null;
  },

  selectedScannerType: config.defaultScanner,
  setSelectedScannerType: (scannerType: ScannerType) =>
    set(() => ({ selectedScannerType: scannerType })),

  /*
  demo scenario
  */
  demoScenario: undefined,
  setDemoScenario: (demoScenario: string) =>
    set(() => ({ demoScenario: demoScenario })),

  /*
  address
  */
  addressLine1: "",
  setAddressLine1: (line1: string) => set(() => ({ addressLine1: line1 })),

  addressState: "",
  setAddressState: (state: string) => set(() => ({ addressState: state })),

  addressCity: "",
  setAddressCity: (city: string) => set(() => ({ addressCity: city })),

  addressPostalCode: "",
  setAddressPostalCode: (postalCode: string) =>
    set(() => ({ addressPostalCode: postalCode })),
});
